<template>
    <div class="max-w-(--breakpoint-sm) flex flex-col gap-4">
        <page-header>
            <div class="flex flex-col">
                FTP Pull Integration
                <p class="mt-1 text-base font-normal">
                    Configure Voxie to connect to your SFTP servers to automatically pull contacts, conversions, and
                    groups data every day.
                </p>
            </div>
        </page-header>

        <page-loading v-if="status === 'loading'" />

        <vx-box
            v-else-if="status === 'not_available'"
            color="info"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            The FTP Pull integration is not available for your team. Please contact Voxie Support if you need access to
            this feature.
        </vx-box>

        <vx-box
            v-else-if="!props.ownsTeam"
            color="info"
            class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
        >
            Only the team owner can configure integrations.
        </vx-box>

        <div v-else class="space-y-4">
            <ftp-pull-data-type-card
                type="contacts"
                :existing="existingConfig?.contacts"
                @configUpdated="handleConfigUpdated"
            />

            <ftp-pull-data-type-card
                type="conversions"
                :existing="existingConfig?.conversions"
                @configUpdated="handleConfigUpdated"
            />

            <ftp-pull-data-type-card
                type="groups"
                :existing="existingConfig?.groups"
                @configUpdated="handleConfigUpdated"
            />
        </div>

        <ftp-pull-documentation />
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { useToasted } from '~/composables/useToasted';
import teamsClient from '~/services/teamsClient';
import FtpPullDocumentation from './ftp-pull/FtpPullDocumentation.vue';
import FtpPullDataTypeCard from './ftp-pull/FtpPullDataTypeCard.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { VxBox } from '@voxie/frontend-components';
import type { FtpPullStatusResponse, FtpPullConfigResponse } from '~/types/ftp-pull.types';
import { HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { catchRequestError } from '~/utils/request';

interface Props {
    ownsTeam: boolean;
}

const props = defineProps<Props>();

const toasted = useToasted();

const status = ref<FtpPullStatusResponse['status']>('loading');
const existingConfig = ref<FtpPullConfigResponse | null>(null);

const loadStatus = async () => {
    try {
        const response = await teamsClient.getFtpPullStatus(Spark.state.currentTeam.id);
        status.value = response.data.status;

        if (status.value === 'connected' || status.value === 'not_connected') {
            await loadConfig();
        }
    } catch (error) {
        toasted.error(catchRequestError(error));
    }
};

const loadConfig = async () => {
    try {
        const response = await teamsClient.getFtpPullConfig(Spark.state.currentTeam.id);
        existingConfig.value = response.data;
    } catch (error) {
        // Deleting the last data type deletes the integration, so a 404 is an empty configuration, not a failed read.
        if ((error as { response?: { status?: number } })?.response?.status === HTTP_NOT_FOUND) {
            existingConfig.value = null;
            return;
        }

        toasted.error(catchRequestError(error));
    }
};

const handleConfigUpdated = async () => {
    await loadStatus();
};

onMounted(() => {
    loadStatus();
});
</script>
